/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
 FC,
 createContext,
 useContext,
 useEffect,
 useState,
} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG, WithChildren} from 'helpers'

export interface PageLink {
 title?: string
 path: string
 isActive: boolean
 isSeparator?: boolean
 className?: string
 onClick?: () => void
}

export interface PageDataContextModel {
 pageTitle?: string
 setPageTitle: (_title: string) => void
 pageDescription?: string
 setPageDescription: (_description: string) => void
 pageBreadcrumbs?: Array<PageLink>
 setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>(
 {
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
 }
)

const PageDataProvider: FC<WithChildren> = ({children}) => {
 const [pageTitle, setPageTitle] = useState<string>('')
 const [pageDescription, setPageDescription] =
  useState<string>('')
 const [pageBreadcrumbs, setPageBreadcrumbs] = useState<
  Array<PageLink>
 >([])
 const value: PageDataContextModel = {
  pageTitle,
  setPageTitle,
  pageDescription,
  setPageDescription,
  pageBreadcrumbs,
  setPageBreadcrumbs,
 }
 return (
  <PageDataContext.Provider value={value}>
   {children}
  </PageDataContext.Provider>
 )
}

function usePageData() {
 return useContext(PageDataContext)
}

type Props = {
 description?: string
 breadcrumbContainerClass?: string
 breadcrumbClass?: string
 breadcrumbIconClass?: string
 showBreadcrumbIcon?: boolean
 breadcrumbs?: Array<PageLink>
}

const PageTitle: FC<Props & WithChildren> = ({
 children,
 description,
 breadcrumbs,
 breadcrumbContainerClass,
 breadcrumbClass,
 breadcrumbIconClass,
 showBreadcrumbIcon = false,
}) => {
 const {
  setPageTitle,
  setPageDescription,
  setPageBreadcrumbs,
 } = usePageData()
 useEffect(() => {
  if (children) {
   setPageTitle(children.toString())
  }
  return () => {
   setPageTitle('')
  }
 }, [children])

 useEffect(() => {
  if (description) {
   setPageDescription(description)
  }
  return () => {
   setPageDescription('')
  }
 }, [description])

 useEffect(() => {
  if (breadcrumbs) {
   setPageBreadcrumbs(breadcrumbs)
  }
  return () => {
   setPageBreadcrumbs([])
  }
 }, [breadcrumbs])

 return (
  <>
   <div
    className={`${breadcrumbContainerClass} d-flex align-items-center`}
   >
    {breadcrumbs
     ? breadcrumbs.map((item: PageLink, i: number) => {
        return (
         <div key={i}>
          {item.path ? (
           <Link to={item.path}>
            <span
             className={`${
              item.className
             } ${breadcrumbClass} ${
              !item.isActive && 'text-[#4E83E4]'
             }`}
            >
             {item.title}
            </span>
            {!item.isActive &&
             (showBreadcrumbIcon ? (
              <KTSVG
               className={`${breadcrumbIconClass}`}
               path={
                '/media/icons/duotone/general/gen03.svg'
               }
              />
             ) : (
              <span
               className={`${breadcrumbIconClass} px-1`}
              >
               /
              </span>
             ))}
           </Link>
          ) : (
           <p>
            <span
             className={`${
              item.className
             } ${breadcrumbClass} ${
              item.onClick && 'cursor-pointer'
             } ${!item.isActive && 'text-[#4E83E4]'}`}
             onClick={item.onClick}
            >
             {item.title}
            </span>
            {!item.isActive &&
             (showBreadcrumbIcon ? (
              <KTSVG
               className={`${breadcrumbIconClass}`}
               path={
                '/media/icons/duotone/general/gen03.svg'
               }
              />
             ) : (
              <span
               className={`${breadcrumbIconClass} px-1`}
              >
               /
              </span>
             ))}
           </p>
          )}
         </div>
        )
       })
     : ''}
   </div>
  </>
 )
}

const PageDescription: FC<WithChildren> = ({children}) => {
 const {setPageDescription} = usePageData()
 useEffect(() => {
  if (children) {
   setPageDescription(children.toString())
  }
  return () => {
   setPageDescription('')
  }
 }, [children])
 return <></>
}

export {
 PageDescription,
 PageTitle,
 PageDataProvider,
 usePageData,
}
